import { useState } from 'react';
import { AppSheet } from '@/components/ui/app-sheet';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/hooks/useAuth';
import { useConfirm } from '@/hooks/useConfirm';
import { useDivisions } from '@/hooks/useDivisions';
import { useTeams } from '@/hooks/useTeams';
import type { VpnPolicy } from '../api';
import { minutesLabel } from '../format';
import { useDeletePolicy, useRegionVpns, useSavePolicy } from '../hooks';
import { useVpnPermissions } from '../permissions';

const MAX_MINUTES = 480;

/**
 * Make or change a policy: these teams may connect to these places (every
 * site, or any divisions and sites) for at most this long. Access is the whole
 * network at each place until finer-grained access ships.
 */
export function PolicySheet({ policy, onClose }: { policy: VpnPolicy | null; onClose: () => void }) {
  const { user } = useAuth();
  const { canUpdateVpn } = useVpnPermissions();
  const teams = useTeams(user?.organizationId);
  const divisions = useDivisions(user?.organizationId);
  const sites = useRegionVpns();
  const save = useSavePolicy();
  const remove = useDeletePolicy();
  const { confirm, confirmDialog } = useConfirm();

  const [name, setName] = useState(policy?.name ?? '');
  const [enabled, setEnabled] = useState(policy?.enabled ?? true);
  const [teamIds, setTeamIds] = useState<string[]>(policy?.teams.map((t) => t.id) ?? []);
  const [wholeOrganization, setWholeOrganization] = useState(policy?.wholeOrganization ?? true);
  const [divisionIds, setDivisionIds] = useState<string[]>(
    policy?.places.filter((p) => p.kind === 'division').map((p) => p.id) ?? [],
  );
  const [regionIds, setRegionIds] = useState<string[]>(
    policy?.places.filter((p) => p.kind === 'region').map((p) => p.id) ?? [],
  );
  const [minutes, setMinutes] = useState(String(policy?.maxSessionMinutes ?? MAX_MINUTES));

  const minutesNumber = Number(minutes);
  const minutesValid = Number.isInteger(minutesNumber) && minutesNumber >= 1 && minutesNumber <= MAX_MINUTES;
  const divisionNames = new Map((divisions.data ?? []).map((d) => [d.id, d.name]));
  /** A site a ticked division (or every site) already covers. */
  const covered = (divisionId: string | null) =>
    wholeOrganization || (divisionId !== null && divisionIds.includes(divisionId));
  // What's ticked and not covered is what's named.
  const namedRegions = regionIds.filter(
    (id) => !covered((sites.data ?? []).find((r) => r.regionId === id)?.divisionId ?? null),
  );
  const placed = wholeOrganization || divisionIds.length > 0 || namedRegions.length > 0;
  const ready = canUpdateVpn && name.trim() !== '' && teamIds.length > 0 && placed && minutesValid;
  const readOnly = !canUpdateVpn;

  const onSave = () => {
    save.mutate(
      {
        id: policy?.id,
        body: {
          name: name.trim(),
          enabled,
          teamIds,
          wholeOrganization,
          divisionIds: wholeOrganization ? [] : divisionIds,
          regionIds: wholeOrganization ? [] : namedRegions,
          maxSessionMinutes: minutesNumber,
        },
      },
      { onSuccess: onClose },
    );
  };

  const onDelete = async () => {
    if (!policy) return;
    const ok = await confirm({
      title: `Delete ${policy.name}?`,
      message: 'Sessions only this policy allowed end now.',
      isDanger: true,
      confirmText: 'Delete',
    });
    if (ok) remove.mutate(policy.id, { onSuccess: onClose });
  };

  const toggle = (id: string, on: boolean) => (ids: string[]) => (on ? [...ids, id] : ids.filter((t) => t !== id));

  return (
    <AppSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="lg"
      title={policy ? policy.name : 'New policy'}
      description="These teams may connect to these places, to their whole network, for at most this long."
    >
      <AppSheet.Body className="space-y-5">
        {readOnly ? <p className="text-xs text-muted-foreground">Changing policies takes vpn:update.</p> : null}
        <div className="grid gap-1.5">
          <Label htmlFor="policy-name">Name</Label>
          <Input
            id="policy-name"
            value={name}
            disabled={readOnly}
            placeholder="Field technicians"
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </div>

        <div className="flex items-center gap-3">
          <Switch id="policy-enabled" checked={enabled} disabled={readOnly} onCheckedChange={setEnabled} />
          <Label htmlFor="policy-enabled">{enabled ? 'On' : 'Off: nobody connects by it'}</Label>
        </div>

        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium mb-1">Teams</legend>
          {(teams.data ?? []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No teams yet.</p>
          ) : (
            (teams.data ?? []).map((team) => (
              <label key={team.id} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={teamIds.includes(team.id)}
                  disabled={readOnly}
                  onCheckedChange={(on) => {
                    setTeamIds(toggle(team.id, on === true));
                  }}
                  aria-label={team.name}
                />
                {team.name}
                {team.divisionName ? <span className="text-xs text-muted-foreground">· {team.divisionName}</span> : null}
              </label>
            ))
          )}
        </fieldset>

        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium mb-1">Where</legend>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={wholeOrganization}
              disabled={readOnly}
              onCheckedChange={(on) => {
                setWholeOrganization(on === true);
              }}
              aria-label="Every site in the organization"
            />
            Every site in the organization
          </label>
          {(divisions.data ?? []).length > 0 ? (
            <div className="grid gap-2 pl-6">
              {(divisions.data ?? []).map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={wholeOrganization || divisionIds.includes(d.id)}
                    disabled={readOnly || wholeOrganization}
                    onCheckedChange={(on) => {
                      setDivisionIds(toggle(d.id, on === true));
                    }}
                    aria-label={`Every site in ${d.name}`}
                  />
                  Every site in {d.name}
                </label>
              ))}
            </div>
          ) : null}
          {(sites.data ?? []).length > 0 ? (
            <div className="grid gap-2 pl-6 max-h-64 overflow-y-auto">
              {(sites.data ?? []).map((r) => (
                <label key={r.regionId} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={covered(r.divisionId) || regionIds.includes(r.regionId)}
                    disabled={readOnly || covered(r.divisionId)}
                    onCheckedChange={(on) => {
                      setRegionIds(toggle(r.regionId, on === true));
                    }}
                    aria-label={r.regionName}
                  />
                  {r.regionName}
                  {r.divisionId && divisionNames.has(r.divisionId) ? (
                    <span className="text-xs text-muted-foreground">· {divisionNames.get(r.divisionId)}</span>
                  ) : null}
                </label>
              ))}
            </div>
          ) : null}
          {placed ? null : <p className="text-xs text-muted-foreground">Tick at least one place.</p>}
        </fieldset>

        <div className="grid gap-1.5">
          <Label htmlFor="policy-minutes">Longest session (minutes)</Label>
          <div className="flex items-center gap-2">
            <Input
              id="policy-minutes"
              className="w-28"
              inputMode="numeric"
              value={minutes}
              disabled={readOnly}
              aria-invalid={!minutesValid}
              onChange={(e) => {
                setMinutes(e.target.value);
              }}
            />
            <span className="text-xs text-muted-foreground">
              {minutesValid ? minutesLabel(minutesNumber) : `From 1 to ${String(MAX_MINUTES)}`}
            </span>
          </div>
        </div>

      </AppSheet.Body>
      <AppSheet.Footer className="sm:justify-between">
        {policy && canUpdateVpn ? (
          <Button variant="ghost" disabled={remove.isPending} onClick={() => void onDelete()}>
            Delete
          </Button>
        ) : (
          <span />
        )}
        <div className="flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {readOnly ? null : (
            <Button disabled={!ready || save.isPending} onClick={onSave}>
              Save
            </Button>
          )}
        </div>
      </AppSheet.Footer>
      {confirmDialog}
    </AppSheet>
  );
}
